import React, {Fragment} from 'react'
import {Link, useParams, useHistory} from 'react-router-dom'
import {pluralise, formatNumber, formatDate} from 'admin/lib/format'
import useInvoices from 'admin/hooks/useInvoices'
import useDocument from 'admin/hooks/useDocument'
import Pagination from 'admin/components/shared/Pagination'
import Tabs from 'admin/components/shared/Tabs'
import Flash from 'admin/components/shared/Flash'
import Filters from './Filters'
import Page from '../shared/Page'
import Sorting from '../shared/Sorting'

const sortOptions = [
  {sortBy: 'date', sortDirection: 'desc', sortLabel: 'Date', ascLabel: 'Oldest first', descLabel: 'Latest first'},
  {
    sortBy: 'due_date',
    sortDirection: 'desc',
    sortLabel: 'Due date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {
    sortBy: 'account_name',
    sortDirection: 'asc',
    sortLabel: 'Account name',
    ascLabel: 'A-Z',
    descLabel: 'Z-A'
  },
  {sortBy: 'invoice_number', sortDirection: 'asc', sortLabel: 'Invoice number'},
  {sortBy: 'amount', sortDirection: 'asc', sortLabel: 'Amount'}
]

export default () => {
  const {organisationID} = useParams()
  const {filters, setFilters, invoices, totalCount} = useInvoices(organisationID)
  const history = useHistory()

  useDocument({title: 'Invoices'})

  const handleRowClick = (e, invoice) => {
    if (e.target.tagName.toLowerCase() !== 'a') {
      e.preventDefault()
      history.push(`/orgs/${organisationID}/invoices/${invoice.id}`)
    }
  }

  return (
    <Fragment>
      <Flash />

      <Tabs
        collection={[
          {label: 'Individual', to: `/orgs/${organisationID}/invoices`},
          {label: 'Repeating', to: `/orgs/${organisationID}/repeating_invoices`}
        ]}
      />

      <Page
        id='admin_invoices'
        title='Manage individual invoices'
        subtitle={
          totalCount ? `Showing ${invoices.length} of ${pluralise(`invoice`, totalCount, true)}` : 'No invoices found'
        }
        buttons={
          <Link to={`/orgs/${organisationID}/invoices/new`} className='button is-light'>
            New invoice
          </Link>
        }>
        <Filters q={filters.q} fromDate={filters.fromDate} toDate={filters.toDate} onSubmit={setFilters} />
        {invoices.length != 0 ? (
          <Fragment>
            <div className='level'>
              <div className='level-left'>
                <Sorting
                  className='level-item'
                  sortOptions={sortOptions}
                  sortBy={filters.sortBy}
                  sortDirection={filters.sortDirection}
                  onChange={setFilters}
                />
              </div>
              <div className='level-right'>
                <Pagination
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                  className='level-item'
                />
              </div>
            </div>

            <table className='table is-striped is-hoverable is-fullwidth is-linked'>
              <colgroup>
                <col width='40%' />
                <col width='10%' />
                <col width='20%' />
                <col width='15%' />
                <col width='15%' />
              </colgroup>
              <thead>
                <tr className='is-size-8'>
                  <th>Account name</th>
                  <th></th>
                  <th>Invoice number</th>
                  <th>Date</th>
                  <th className='has-text-right'>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, i) => (
                  <tr key={i} onClick={e => handleRowClick(e, invoice)}>
                    <td>
                      <Link to={`/orgs/${organisationID}/accounts/${invoice.account.id}`}>
                        {invoice.account.displayName}
                      </Link>
                      {invoice.account.displayName !== invoice.account.contactFullName &&
                      !!invoice.account.contactFullName ? (
                        <sub className='has-text-weight-semibold'>{invoice.account.contactFullName}</sub>
                      ) : null}
                    </td>
                    <td className='has-text-right'>
                      {invoice.xeroLink && (
                        <span className='tag is-small has-text-white' style={{backgroundColor: '#51afd8'}}>
                          Xero
                        </span>
                      )}
                      {invoice.state === 'draft' ? (
                        <span className='tag is-small has-background-grey-light'>Draft</span>
                      ) : invoice.isSent ? (
                        <span className='tag is-success is-small'>Issued</span>
                      ) : invoice.isSendable ? (
                        <span className='tag is-success is-light is-small'>Send</span>
                      ) : null}
                    </td>
                    <td>{invoice.invoiceNumber}</td>
                    <td>
                      <span>{formatDate(invoice.date)}</span>
                      <sub className='has-text-weight-semibold'>Due: {formatDate(invoice.dueDate)}</sub>
                    </td>
                    <td className='has-text-right'>{formatNumber(invoice.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {invoices.length > 10 && (
              <div className='level'>
                <div className='level-left'></div>
                <div className='level-right'>
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={totalCount}
                    onChange={setFilters}
                    className='level-item'
                  />
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
